import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { Fragment, Component } from 'react';
import { omit, debounce } from '../../../utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as tracker from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { upgradeDataPropsInterface } from 'ui-addon-upgrades/_core/common/data/upgradeData/interfaces/upgradeDataPropsInterface';
import { TrialActivationButtonPropsInterface } from 'ui-addon-upgrades/_core/trial/TrialPropsInterface';
import AdminOnlyWrapper from 'ui-addon-upgrades/_core/common/components/AdminOnlyWrapper';
import { TEAM } from 'ui-addon-upgrades/_core/common/constants/AdminTypes';
import { REQUEST_TRIAL } from 'ui-addon-upgrades/_core/common/constants/AdminModalTypes';
import TrialActivationModalContainer from './TrialActivationModalContainer';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
const CLICK_DELAY = 50;

function sequence(...handlers) {
  return (...args) => {
    handlers.forEach(handler => handler && handler(...args));
  };
}

class TrialActivationButton extends Component {
  constructor(props) {
    super(props);

    this.handleClick = () => {
      const {
        activeTrials,
        expiredTrials,
        isMultiTrial,
        upgradeData,
        isUpgradePoint,
        level
      } = this.props;
      tracker.track('trialActivationButtonInteraction', Object.assign({
        action: 'clicked'
      }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {
        isMultiTrial,
        activeTrials,
        expiredTrials,
        trialId: upgradeData.trialId,
        duration: upgradeData.duration
      }));

      if (isUpgradePoint) {
        tracker.track('interaction', Object.assign({
          action: 'clicked upgrade point'
        }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint)));
        tracker.track('interaction', Object.assign({
          action: 'clicked trial activation',
          isPrimaryCta: level === 1,
          isSecondaryCta: level === 2
        }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint)));
      }

      this.setState({
        showActivationModalContainer: true
      });
    };

    this.state = {
      showActivationModalContainer: false
    };
    this.debouncedHandleClick = debounce(this.handleClick, CLICK_DELAY);
  }

  componentDidMount() {
    const {
      activeTrials,
      expiredTrials,
      isMultiTrial,
      upgradeData,
      isUpgradePoint,
      level
    } = this.props;
    tracker.track('trialActivationButtonInteraction', Object.assign({
      action: 'viewed'
    }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {
      isMultiTrial,
      activeTrials,
      expiredTrials,
      trialId: upgradeData.trialId,
      duration: upgradeData.duration
    }));

    if (isUpgradePoint) {
      tracker.track('interaction', Object.assign({
        action: 'viewed trial activation',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint)));
    }
  }

  componentWillUnmount() {
    this.debouncedHandleClick.reset();
  }

  render() {
    const {
      activeTrials,
      expiredTrials,
      hideBackground,
      isMultiTrial,
      upgradeData: {
        trialId,
        upgradeProduct,
        apiName
      },
      upgradeData,
      onClick = () => {},
      onSuccess,
      shouldHideSurvey,
      successModalHeader,
      successModalSubText,
      successModalCtaText,
      showSuccessModalIllustration,
      children,
      canViewNewSeats,
      isTX0120Variant = false,
      isTX0120Fetched = false,
      isEligibleForTX0120 = false,
      isUpgradeModal = false
    } = this.props;
    const trialI81nKey = canViewNewSeats && apiName ? `upgrades.trialActivationButton.${apiName}` : `upgrades.trialActivationButton.${upgradeProduct}`;
    const isTrialed = [...(activeTrials || []), ...(expiredTrials || [])].includes(upgradeProduct);
    const {
      showActivationModalContainer
    } = this.state;
    const onClickSequence = sequence(this.debouncedHandleClick, onClick);
    const propsToPass = omit(this.props, ['onClick', 'upgradeData', 'onSuccess', 'shouldHideSurvey', 'trialId', 'allowModal', 'successModalHeader', 'successModalSubText', 'successModalCtaText', 'showSuccessModalIllustration', 'isMultiTrial', 'activeTrials', 'expiredTrials', 'isUpgradePoint', 'hideBackground', 'canViewNewSeats', 'isTX0120Variant', 'isTX0120Fetched', 'isEligibleForTX0120', 'isUpgradeModal']);
    const isTX0120Loading = !!isEligibleForTX0120 && !isTX0120Fetched;
    if (!trialId) return null;

    const DisabledToolTipWrapper = ({
      children: wrapperChildren,
      disabled
    }) => {
      if (disabled) {
        return /*#__PURE__*/_jsx(UITooltip, {
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.trialActivationButton.tooltip.disabledButton",
            options: {
              upgradeProduct: I18n.text(`upgrades.products.${upgradeProduct}`)
            }
          }),
          children: wrapperChildren
        });
      }

      return /*#__PURE__*/_jsx(_Fragment, {
        children: wrapperChildren
      });
    };

    return /*#__PURE__*/_jsxs(Fragment, {
      children: [showActivationModalContainer && /*#__PURE__*/_jsx(TrialActivationModalContainer, {
        activeTrials: activeTrials,
        expiredTrials: expiredTrials,
        hideBackground: hideBackground,
        isMultiTrial: isMultiTrial,
        upgradeData: upgradeData,
        shouldHideSurvey: shouldHideSurvey,
        onSuccess: onSuccess,
        successModalHeader: successModalHeader,
        successModalSubText: successModalSubText,
        successModalCtaText: successModalCtaText,
        showSuccessModalIllustration: showSuccessModalIllustration,
        canViewNewSeats: canViewNewSeats,
        isTX0120Variant: isTX0120Variant,
        isTX0120Fetched: isTX0120Fetched,
        isEligibleForTX0120: isEligibleForTX0120,
        isUpgradeModal: isUpgradeModal
      }), /*#__PURE__*/_jsx(AdminOnlyWrapper, {
        upgradeData: upgradeData,
        adminType: TEAM,
        modalType: REQUEST_TRIAL,
        canViewNewSeats: canViewNewSeats,
        children: /*#__PURE__*/_jsx(DisabledToolTipWrapper, {
          disabled: isTrialed,
          children: propsToPass.use && propsToPass.use.includes('link') ? /*#__PURE__*/_jsx(UIButton, Object.assign({}, propsToPass, {
            onClick: onClickSequence,
            "data-selenium": "trial-button",
            "data-test-id": "trial-button",
            "data-reagan-primary-cta": true,
            disabled: isTrialed,
            children: children || /*#__PURE__*/_jsx(FormattedMessage, {
              message: trialI81nKey
            })
          })) : /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, propsToPass, {
            onClick: onClickSequence,
            "data-selenium": "trial-button",
            "data-test-id": "trial-loading-button",
            "data-reagan-primary-cta": true,
            loading: showActivationModalContainer || isTX0120Loading,
            disabled: isTrialed,
            children: children || /*#__PURE__*/_jsx(FormattedMessage, {
              message: trialI81nKey
            })
          }))
        })
      })]
    });
  }

}

TrialActivationButton.defaultProps = {
  use: 'primary',
  onClick: () => {}
}; // @ts-expect-error type is too long

TrialActivationButton.propTypes = Object.assign({}, upgradeDataPropsInterface, {}, TrialActivationButtonPropsInterface, {
  activeTrials: PropTypes.array.isRequired,
  expiredTrials: PropTypes.array.isRequired,
  children: PropTypes.node,
  isMultiTrial: PropTypes.bool.isRequired,
  onClick: PropTypes.func
});
export default TrialActivationButton;